import { AxiosResponse } from 'axios';
import { config } from 'config/config.local';
import { CONTENT_BACKOFFICE } from './axios';

const DEFAULT_CUSTOM_CONTENT_PAGE = 1;
const DEFAULT_CUSTOM_CONTENT_PAGE_SIZE = 10;

const toPositiveNumber = (value: number | string | null | undefined, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export interface ICustomContentMetadata {
  image_url?: string | null;
  [key: string]: unknown;
}

export interface ICustomContentData {
  name?: string;
  description?: string;
  difficulty?: string;
  duration_in_seconds?: number | string | null;
  duration?: number | string | null;
  published_date?: string | null;
  metadata?: ICustomContentMetadata | null;
  instructors?: Array<{ name?: string } | string> | null;
  learning_areas?: Array<{ name?: string } | string> | null;
  cpe_credits?: number | null;
  is_lab_experience?: boolean | null;
  [key: string]: unknown;
}

export interface ICustomContentItem {
  id: string;
  content_type?: string | null;
  content_id?: string | null;
  business_account_id?: string | null;
  visibility?: string | null;
  created?: string | null;
  modified?: string | null;
  content_data?: ICustomContentData | null;
  [key: string]: unknown;
}

export type ICustomContentVisibility = 'limited' | 'unlimited';

export interface IUpdateCustomContentVisibilityPayload {
  visibility: ICustomContentVisibility;
}

export interface IUpdateCustomContentVisibilityResponse {
  id: string;
  visibility: ICustomContentVisibility;
  updated_at: string;
}

export interface ICustomContentListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ICustomContentItem[];
}

export interface ICustomContentListParams {
  page?: number | string | null;
  page_size?: number | string | null;
  deep_details?: boolean;
  search?: string | null;
  content_type?: string | string[] | null;
}

export interface ICustomContentMetricsParams {
  custom_content_ids?: string[];
}

export interface ICustomContentMetric {
  key: string;
  label: string;
  value: string;
  tooltip?: string;
}

export type ICustomContentMetricsResponse = Record<string, ICustomContentMetric[]>;

export const CUSTOM_CONTENT_METRICS_KEYS = {
  CONTENT_ID: 'content_id',
  CONTENT_TYPE: 'content_type',
  HAS_CERTIFICATION: 'has_certification',
  CERTIFICATION_ID: 'certification_id',
  CERTIFICATION_NAME: 'certification_name',
  ENGAGED_USERS: 'engaged_users',
  COMPLETION_RATE: 'completion_rate',
  AVG_PROGRESS: 'avg_progress',
  TOTAL_STARTS: 'total_starts',
  AVG_SESSION_DURATION: 'avg_session_duration',
  AVG_FLAGS_CAPTURED: 'avg_flags_captured',
  AVG_SCORE: 'avg_score',
  ATTEMPTS: 'attempts',
  PASS_RATE: 'pass_rate',
  IMPROVEMENT_RATE: 'improvement_rate',
  EXAM_PARTICIPATION_RATE: 'exam_participation_rate',
  EXAM_PASS_RATE: 'exam_pass_rate',
  FULL_COMPLETION_RATE: 'full_completion_rate',
} as const;

export interface ICustomContentMetricsItem {
  content_id: string;
  content_type: string;
  has_certification?: boolean;
  certification_id?: string;
  certification_name?: string;
  engaged_users?: number | string;
  completion_rate?: string;
  avg_progress?: string;
  total_starts?: string;
  avg_session_duration?: string;
  avg_flags_captured?: number | string;
  avg_score?: string;
  attempts?: number | string;
  pass_rate?: string;
  improvement_rate?: string;
  exam_participation_rate?: string;
  exam_pass_rate?: string;
  full_completion_rate?: string;
  [key: string]: unknown;
}

export type ICustomContentMetricsApiResponse = ICustomContentMetricsItem[];

export const getCustomContentList = (
  params: ICustomContentListParams = {}
): Promise<AxiosResponse<ICustomContentListResponse>> => {
  const page = toPositiveNumber(params.page, DEFAULT_CUSTOM_CONTENT_PAGE);
  const page_size = toPositiveNumber(params.page_size, DEFAULT_CUSTOM_CONTENT_PAGE_SIZE);
  const search = typeof params.search === 'string' ? params.search.trim() : '';
  const contentTypeParam = Array.isArray(params.content_type)
    ? params.content_type.filter(Boolean).join(',')
    : params.content_type;
  const content_type =
    typeof contentTypeParam === 'string' ? contentTypeParam.trim() : contentTypeParam;

  return CONTENT_BACKOFFICE.get(config.routes.customContent.getCustomContent, {
    params: {
      page,
      page_size,
      ...(typeof params.deep_details === 'boolean' ? { deep_details: params.deep_details } : {}),
      ...(search ? { search } : {}),
      ...(content_type ? { content_type } : {}),
    },
  });
};

export const getCustomContentMetrics = (
  payload: ICustomContentMetricsParams = {}
): Promise<AxiosResponse<ICustomContentMetricsApiResponse>> => {
  return CONTENT_BACKOFFICE.post(config.routes.customContent.metrics, payload);
};

const getCustomContentExistence = (): Promise<AxiosResponse<ICustomContentListResponse>> => {
  return getCustomContentList({
    page: 1,
    page_size: 1,
    deep_details: false,
  });
};

const resolveCustomContentExistence = (data: ICustomContentListResponse): boolean => {
  if (typeof data?.count === 'number') return data.count > 0;
  if (Array.isArray(data?.results)) return data.results.length > 0;
  return false;
};

export const hasCustomContent = async (customContentEnabled?: boolean): Promise<boolean> => {
  if (customContentEnabled === false) {
    return false;
  }

  const { data } = await getCustomContentExistence();
  return resolveCustomContentExistence(data);
};

export const updateCustomContentVisibility = async (
  id: string,
  payload: IUpdateCustomContentVisibilityPayload
): Promise<AxiosResponse<IUpdateCustomContentVisibilityResponse>> => {
  const customContentBaseRoute = config.routes.customContent.getCustomContent.replace(/\/+$/, '');
  return CONTENT_BACKOFFICE.patch(`${customContentBaseRoute}/${id}/visibility`, payload);
};
